import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Share2, Users, TrendingUp, Gift, Star, LogOut,
  CheckCircle2, Clock, Phone, MapPin, Award, Zap,
  MessageCircle, ChevronRight, RefreshCw, QrCode, Bell, UserCheck
} from "lucide-react";

export default function MitraDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const referralLink = `https://hamarsathi.com/?ref=${user?.referral_code || ""}`;

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, refRes, notifRes] = await Promise.allSettled([
        api.get("/mitra/my-stats"),
        api.get("/mitra/my-referrals"),
        api.get("/notifications"),
      ]);
      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      if (refRes.status === "fulfilled") setReferrals(refRes.value.data || []);
      if (notifRes.status === "fulfilled") setNotifications(notifRes.value.data?.notifications || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || "");
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `🌸 Jharkhand ke best matrimonial platform par aayein!\n\nHamarsathi.com par apna rishta dhundein — Free registration!\n\n🔗 ${referralLink}\n\nCode: *${user?.referral_code}*\n\n— ${user?.name}, Vivah Mitra`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const statCards = [
    {
      label: "कुल Referrals",
      value: stats?.total_referrals ?? user?.referral_count ?? 0,
      icon: <Users className="w-5 h-5" />,
      color: "from-emerald-500 to-teal-600",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    },
    {
      label: "इस महीने",
      value: stats?.monthly_referrals ?? 0,
      icon: <TrendingUp className="w-5 h-5" />,
      color: "from-blue-500 to-indigo-600",
      bg: "bg-blue-50",
      text: "text-blue-700",
    },
    {
      label: "Verified Profiles",
      value: stats?.verified_referrals ?? 0,
      icon: <CheckCircle2 className="w-5 h-5" />,
      color: "from-violet-500 to-purple-600",
      bg: "bg-violet-50",
      text: "text-violet-700",
    },
    {
      label: "कमाई (₹)",
      value: `₹${stats?.total_earnings ?? 0}`,
      icon: <Gift className="w-5 h-5" />,
      color: "from-amber-500 to-orange-600",
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0a0e] via-[#2d1018] to-[#1a0a0e]">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 bg-black/30 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
              {(user?.name || "M")[0].toUpperCase()}
            </div>
            <div>
              <div className="text-white font-semibold text-sm leading-none">{user?.name || "Mitra"}</div>
              <div className="text-amber-400 text-xs mt-0.5 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400" />
                {user?.mitra_level || "District Ambassador"} · {user?.district || ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button onClick={handleLogout} className="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500/30 flex items-center justify-center text-white transition-all" title="Logout">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* Hero Banner */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-rose-600 p-6 shadow-2xl">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-2 right-4 text-8xl">🌸</div>
            <div className="absolute bottom-2 left-4 text-6xl">💍</div>
          </div>
          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full mb-3">
              <Award className="w-3.5 h-3.5" />
              Vivah Mitra Ambassador
            </div>
            <h1 className="text-white text-2xl font-bold mb-1">
              Namaskar, {(user?.name || "Mitra").split(" ")[0]} ji! 🙏
            </h1>
            <p className="text-white/80 text-sm">
              Aapka referral code share karein aur logo ki shaadi mein madad karein
            </p>
          </div>
        </div>

        {/* Referral Code Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <QrCode className="w-4 h-4 text-amber-400" />
            <span className="text-white font-semibold text-sm">Aapka Referral Code</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 bg-white/10 rounded-xl px-4 py-3 border border-white/20">
              <div className="text-amber-400 font-mono font-bold text-xl tracking-widest text-center">
                {user?.referral_code || "—"}
              </div>
            </div>
            <button
              onClick={copyCode}
              className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all ${
                copied
                  ? "bg-emerald-500 text-white"
                  : "bg-amber-500 hover:bg-amber-400 text-black"
              }`}
            >
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={copyLink}
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2.5 rounded-xl transition-all border border-white/10"
            >
              <Copy className="w-4 h-4" />
              Link Copy
            </button>
            <button
              onClick={shareWhatsApp}
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b858] text-white text-sm font-medium py-2.5 rounded-xl transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Share
            </button>
          </div>
          <div className="mt-3 bg-white/5 rounded-xl px-3 py-2">
            <p className="text-white/50 text-xs text-center truncate">{referralLink}</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br ${s.color} text-white mb-3 shadow-lg`}>
                {s.icon}
              </div>
              <div className="text-white text-2xl font-bold">{loading ? "—" : s.value}</div>
              <div className="text-white/50 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 bg-white/5 p-1 rounded-xl">
          {[
            { id: "overview", label: "📊 Overview" },
            { id: "referrals", label: "👥 Referrals" },
            { id: "share", label: "📤 Share" },
            { id: "notifications", label: "🔔 Updates" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                activeTab === t.id
                  ? "bg-amber-500 text-black shadow"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            {/* Mitra Info Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-amber-400" /> Aapki Mitra Profile
              </h3>
              <div className="space-y-3">
                {[
                  { label: "Naam", value: user?.name, icon: "👤" },
                  { label: "Phone", value: user?.phone, icon: "📱" },
                  { label: "District", value: user?.district, icon: "📍" },
                  { label: "Level", value: user?.mitra_level || "District Ambassador", icon: "🎖️" },
                  { label: "Badge", value: user?.mitra_badge || "District Ambassador", icon: "🏅" },
                  { label: "Referral Code", value: user?.referral_code, icon: "🎗️" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-sm">{item.icon} {item.label}</span>
                    <span className="text-white text-sm font-medium">{item.value || "—"}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> Kaise Kamaein?
              </h3>
              <div className="space-y-3">
                {[
                  { step: "1", text: "Apna referral code share karein apne district mein", color: "bg-blue-500" },
                  { step: "2", text: "Log aapke code se register karein", color: "bg-violet-500" },
                  { step: "3", text: "Unka profile 70%+ complete hone par referral valid hoga", color: "bg-emerald-500" },
                  { step: "4", text: "Har valid referral par commission milega!", color: "bg-amber-500" },
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full ${s.color} flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5`}>
                      {s.step}
                    </div>
                    <p className="text-white/70 text-sm">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "referrals" && (
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-12 text-white/40">Loading...</div>
            ) : referrals.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                <Users className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">Abhi tak koi referral nahi hua.</p>
                <p className="text-white/30 text-xs mt-1">Apna code share karein!</p>
              </div>
            ) : (
              referrals.map((r, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {(r.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium text-sm truncate">{r.name || "Naya Member"}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-white/40 text-xs">{r.district || ""}</span>
                      {r.profile_pct >= 70 ? (
                        <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">✓ Valid</span>
                      ) : (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">{r.profile_pct || 0}% complete</span>
                      )}
                    </div>
                  </div>
                  <div className="text-white/30 text-xs">{r.joined_ago || ""}</div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "share" && (
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Share2 className="w-4 h-4 text-amber-400" /> WhatsApp Message Templates
              </h3>
              {[
                {
                  title: "🌸 General Invite",
                  msg: `Namaskar! Jharkhand ke best matrimonial platform Hamarsathi.com par Free mein register karein. Apna rishta dhundein.\n\n🔗 ${referralLink}\n\nCode: ${user?.referral_code}`,
                },
                {
                  title: "👨‍👩‍👧 Family Message",
                  msg: `Agar aapke ghar mein koi vivah yogya candidate hai, toh Hamarsathi.com par profile banayein.\n\nHamara referral code use karein: ${user?.referral_code}\n🔗 ${referralLink}`,
                },
                {
                  title: "📢 Village/Mohalla",
                  msg: `${user?.district} ke sabhi logo ko — Hamarsathi.com par aayein. Jharkhand ka apna matrimonial portal. Bilkul FREE!\n\nCode: ${user?.referral_code}\n🔗 ${referralLink}`,
                },
              ].map((t, i) => (
                <div key={i} className="mb-4 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-medium">{t.title}</span>
                    <button
                      onClick={() => {
                        const msg = encodeURIComponent(t.msg);
                        window.open(`https://wa.me/?text=${msg}`, "_blank");
                      }}
                      className="text-xs bg-[#25D366] text-white px-3 py-1.5 rounded-lg font-medium"
                    >
                      Send
                    </button>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed">{t.msg.substring(0, 100)}...</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "notifications" && (
          <div className="space-y-3">
            {notifications.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                <Bell className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">Koi notification nahi hai.</p>
              </div>
            ) : (
              notifications.slice(0, 15).map((n, i) => (
                <div key={i} className={`bg-white/5 border rounded-xl p-4 ${!n.read ? "border-amber-500/30" : "border-white/10"}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                      <Bell className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{n.title}</div>
                      <div className="text-white/50 text-xs mt-1 leading-relaxed">{n.text}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Bottom spacing */}
        <div className="h-6" />
      </div>
    </div>
  );
}
